const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8005'

export const getAllDrafts = async () => {
  const response = await fetch(`${API_BASE}/drafts`)
  if (!response.ok) {
    throw new Error(`Failed to fetch drafts: ${response.status}`)
  }
  const data = await response.json()
  return Array.isArray(data) ? data : data.data || data.drafts || []
}

export const getDraftsPaginated = async (page, date, name, status) => {
  const params = new URLSearchParams({
    page,
    size: 10,
    ...(date && { date }),
    ...(name && { name }),
    ...(status && { status })
  })

  const response = await fetch(`${API_BASE}/drafts/history?${params}`)
  if (!response.ok) {
    throw new Error(`Failed to fetch drafts: ${response.status}`)
  }
  return await response.json()
}

export const getDraftById = async (id, selected_nyukabango, options = {}) => {
  let url = `${API_BASE}/draft/${id}`

  if (selected_nyukabango?.length) {
    const selected = Array.isArray(selected_nyukabango)
      ? selected_nyukabango
      : [selected_nyukabango]
    const params = new URLSearchParams({ selected_nyukabango: selected.join(',') })
    url += `?${params.toString()}`
  }

  const response = await fetch(url, options)

  if (!response.ok) {
    throw new Error(`Failed to fetch draft ${id}: ${response.status}`)
  }

  return await response.json()
}

export const getDraftBySelection = async (id, selectedNyukabango = [], options = {}) => {
  const response = await fetch(`${API_BASE}/draft/${id}/selection`, {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: JSON.stringify({ selected_nyukabango: selectedNyukabango })
  })
  if (!response.ok) throw new Error(`Failed to fetch draft ${id}: ${response.status}`)
  return await response.json()
}

export const createDraftGroup = async (sourceDraftId, selectedItems) => {
  const response = await fetch(`${API_BASE}/draft/${sourceDraftId}/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selected_items: selectedItems })
  })
  if (!response.ok) throw new Error(`Failed to create popup draft: ${response.status}`)
  return await response.json()
}

export const createDraft = async (draftData) => {
  const response = await fetch(`${API_BASE}/draft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(draftData)
  })
  if (!response.ok) {
    throw new Error(`Failed to create draft: ${response.status}`)
  }
  return await response.json()
}

export const getPrices = async (draftData) => {
  const response = await fetch(`${API_BASE}/draft/prices`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(draftData)
  })
  if (!response.ok) {
    throw new Error(`Failed to create draft: ${response.status}`)
  }
  return await response.json()
}

export const createDraftFromQuery = async (params) => {
  const queryString = new URLSearchParams(params).toString()
  const response = await fetch(`${API_BASE}/create-draft?${queryString}`, {
    method: 'POST'
  })
  if (!response.ok) {
    throw new Error(`Failed to create draft from query: ${response.status}`)
  }
  return await response.json()
}

export const getCandidates = async (params) => {
  const queryString = new URLSearchParams(params).toString()
  const response = await fetch(`${API_BASE}/candidates?${queryString}`)
  if (!response.ok) {
    throw new Error(`Failed to fetch candidates: ${response.status}`)
  }
  return await response.json()
}

export const createDraftFromSelection = async (payload) => {
  const response = await fetch(`${API_BASE}/create-draft-from-selection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })
  if (!response.ok) {
    throw new Error(`Failed to create draft from selection: ${response.status}`)
  }
  return await response.json()
}

export const allocateProducts = async (draftId) => {
  const response = await fetch(`${API_BASE}/allocate/${draftId}`, {
    method: 'POST'
  })
  if (!response.ok) {
    throw new Error(`Failed to allocate products: ${response.status}`)
  }
  return await response.json()
}

export const reAllocateProducts = async (draftId) => {
  const response = await fetch(`${API_BASE}/${draftId}/reallocate`, {
    method: 'POST'
  })
  if (!response.ok) {
    throw new Error(`Failed to reallocate products: ${response.status}`)
  }
  return await response.json()
}

export const updateDraftInput = async (draftId, inputData) => {
  const response = await fetch(`${API_BASE}/draft/${draftId}/input`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(inputData)
  })
  if (!response.ok) {
    throw new Error(`Failed to update draft input: ${response.status}`)
  }
  return await response.json()
}

export const updateDraftDistribution = async (draftId, inputData, expectedUpdatedAt) => {
  const response = await fetch(`${API_BASE}/update-draft-output/${draftId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(expectedUpdatedAt ? { 'X-Draft-Updated-At': expectedUpdatedAt } : {})
    },
    body: JSON.stringify(inputData)
  })
  if (!response.ok) {
    let errorData = { message: `Failed to update draft: ${response.status}` }
    try {
      const errorJson = await response.json()
      if (errorJson?.detail) {
        errorData = errorJson.detail
      }
    } catch (e) {}
    const error = new Error(errorData.message || 'Validation failed')
    error.data = errorData
    throw error
  }
  return await response.json()
}

export const validateCustomerCodes = async (codes, sellerCode) => {
  const payload = { codes }
  if (sellerCode) payload.seller_code = sellerCode
  const response = await fetch(`${API_BASE}/draft/validate/customer-codes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    throw new Error(`Failed to validate customer codes: ${response.status}`)
  }

  return await response.json()
}

export const validateBranchCodes = async (pairs) => {
  const response = await fetch(`${API_BASE}/draft/validate/branch-codes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ pairs })
  })

  if (!response.ok) {
    throw new Error(`Failed to validate branch codes: ${response.status}`)
  }

  return await response.json()
}

export const getGradeOptions = async (query = '', options = {}) => {
  const params = new URLSearchParams()
  if (query) params.set('query', query)

  const response = await fetch(
    `${API_BASE}/draft/masters/grades${params.toString() ? `?${params}` : ''}`,
    options
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch grade options: ${response.status}`)
  }

  return await response.json()
}

export const getSizeOptions = async (query = '', options = {}) => {
  const params = new URLSearchParams()
  if (query) params.set('query', query)

  const response = await fetch(
    `${API_BASE}/draft/masters/sizes${params.toString() ? `?${params}` : ''}`,
    options
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch size options: ${response.status}`)
  }

  return await response.json()
}

export const saveScheduledAllocation = async (allocationId, data) => {
  const response = await fetch(
    `${API_BASE}/save-scheduled?allocation_id=${allocationId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  )
  if (!response.ok) {
    throw new Error(`Failed to save scheduled allocation: ${response.status}`)
  }
  return await response.json()
}

export const deleteDraft = async (draftId) => {
  const response = await fetch(`${API_BASE}/draft/${draftId}`, {
    method: 'DELETE'
  })
  if (!response.ok) {
    throw new Error(`Failed to delete draft: ${response.status}`)
  }
  return await response.json()
}

export const exportDraftToExcel = async (draftId, selectedNyukabango = null) => {
  const params = new URLSearchParams()
  if (Array.isArray(selectedNyukabango) && selectedNyukabango.length > 0) {
    params.set('selected_nyukabango', selectedNyukabango.join(','))
  }
  const query = params.toString()
  const response = await fetch(
    `${API_BASE}/draft/${draftId}/export/excel${query ? `?${query}` : ''}`
  )
  if (!response.ok) {
    throw new Error(`Failed to export draft to Excel: ${response.status}`)
  }
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get('content-disposition')
  }
}

export const exportDraftToCSV = async (draftId, selectedNyukabango = null) => {
  const params = new URLSearchParams()
  if (Array.isArray(selectedNyukabango) && selectedNyukabango.length > 0) {
    params.set('selected_nyukabango', selectedNyukabango.join(','))
  }
  const query = params.toString()
  const response = await fetch(
    `${API_BASE}/draft/${draftId}/export/csv${query ? `?${query}` : ''}`
  )
  if (!response.ok) {
    throw new Error(`Failed to export draft to CSV: ${response.status}`)
  }
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get('content-disposition')
  }
}

export const uploadDraftToGcpCSV = async (draftId, selectedNyukabango = null) => {
  const params = new URLSearchParams()
  params.set('destination', 'gcp')
  if (Array.isArray(selectedNyukabango) && selectedNyukabango.length > 0) {
    params.set('selected_nyukabango', selectedNyukabango.join(','))
  }
  const response = await fetch(`${API_BASE}/draft/${draftId}/export/csv?${params}`)
  if (response.status === 204) {
    return { status: 204 }
  }
  if (!response.ok) {
    throw new Error(`Failed to upload CSV to GCP: ${response.status}`)
  }
  return await response.json()
}

export const exportDraftToDBExcel = async (draftId, selectedNyukabango = null) => {
  const params = new URLSearchParams()
  if (Array.isArray(selectedNyukabango) && selectedNyukabango.length > 0) {
    params.set('selected_nyukabango', selectedNyukabango.join(','))
  }
  const query = params.toString()
  const response = await fetch(
    `${API_BASE}/draft/${draftId}/export/db_excel${query ? `?${query}` : ''}`
  )
  if (!response.ok) {
    throw new Error(`Failed to export draft to DB Excel: ${response.status}`)
  }
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get('content-disposition')
  }
}

export const uploadDraftToGcpDBExcel = async (draftId, selectedNyukabango = null) => {
  const params = new URLSearchParams()
  params.set('destination', 'gcp')
  if (Array.isArray(selectedNyukabango) && selectedNyukabango.length > 0) {
    params.set('selected_nyukabango', selectedNyukabango.join(','))
  }
  const response = await fetch(
    `${API_BASE}/draft/${draftId}/export/db_excel?${params}`
  )
  if (response.status === 204) {
    return { status: 204 }
  }
  if (!response.ok) {
    throw new Error(`Failed to upload DB Excel to GCP: ${response.status}`)
  }
  return await response.json()
}

export const extractFilenameFromContentDisposition = (
  contentDisposition,
  draftId = null
) => {
  const timestamp = Date.now()
  let filename = ''

  if (!contentDisposition) {
    filename = draftId
      ? `draft_${draftId}_report.xlsx`
      : 'allocation_report.xlsx'
  } else {
    const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/)
    if (rfc5987Match) {
      try {
        filename = decodeURIComponent(rfc5987Match[1])
      } catch (e) {
        console.error('Error decoding RFC 5987 filename:', e)
      }
    }

    if (!filename) {
      const traditionalMatch = contentDisposition.match(/filename="([^"]+)"/)
      if (traditionalMatch) {
        filename = traditionalMatch[1]
      }
    }

    if (!filename) {
      filename = draftId
        ? `draft_${draftId}_report.xlsx`
        : 'allocation_report.xlsx'
    }
  }

  const extMatch = filename.match(/(\.[^.]*)$/)
  if (extMatch) {
    const ext = extMatch[1]
    const base = filename.replace(ext, '')
    return `${base}_${timestamp}${ext}`
  }

  return `${filename}_${timestamp}`
}
